import { inject, Injectable, Injector, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { navigateTo } from '../utils/reload';
import { ClockSyncService } from './clock-sync.service';
import { CustomSyncService } from './custom-sync.service';
import { DatabaseService } from './database.service';
import { LoggerService } from './logger.service';
import { OrganizationService } from './organization.service';
import { PermissionService } from './permission.service';
import { QaBackdoorService } from './qa-backdoor.service';
import { SessionStorageService } from './session-storage.service';
import { SyncInitService } from './sync-init.service';
import { ToastService } from './toast.service';
import { TranslationService } from './translation.service';
import { LOCAL_STORAGE, UserStorageService } from './user-storage.service';
import { UserService } from './user.service';

export interface User {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
}

export interface JwtPayload {
  sub: string;
  customer_id: string;
  email: string;
  full_name: string;
  iat: number;
  exp: number;
  is_new_signup?: boolean; // Flag to indicate first-time org creator
  permissions?: Record<string, 'allow' | 'deny'>; // User permissions
  isSuperAdmin?: boolean; // Super-admin flag
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private router = inject(Router);
  private toast = inject(ToastService);
  private translate = inject(TranslationService);
  private logger = inject(LoggerService).getLogger('auth');
  private injector = inject(Injector);
  private userStorage = inject(UserStorageService);
  private sessionStorage = inject(SessionStorageService);
  private permissionService = inject(PermissionService);
  private localStorage = inject(LOCAL_STORAGE);
  private qaBackdoor = inject(QaBackdoorService);
  private clockSync = inject(ClockSyncService);

  // Lazy-loaded to avoid triggering database initialization at app bootstrap
  private get db(): DatabaseService {
    return this.injector.get(DatabaseService);
  }

  // Reactive state signals
  currentUser = signal<User | null>(null);
  isAuthenticated = signal(false);
  currentOrganization = signal<string | null>(null);

  private readonly TOKEN_KEY = 'auth_token';
  private readonly DEACTIVATION_NOTIFIED_KEY = 'deactivation_notified';
  private handleCallbackInProgress = false;

  // Lazy-loaded to avoid circular dependency
  private get syncService(): SyncInitService {
    return this.injector.get(SyncInitService);
  }

  // Lazy-loaded to avoid circular dependency (UserService → DatabaseService → AuthService)
  private get userService(): UserService {
    return this.injector.get(UserService);
  }

  private get organizationService(): OrganizationService {
    return this.injector.get(OrganizationService);
  }

  // Lazy-loaded to avoid circular dependency
  private get customSyncService(): CustomSyncService {
    return this.injector.get(CustomSyncService);
  }

  /**
   * Clear all cached user/org state from previous login sessions.
   * MUST be called before setting new auth state in any login pathway.
   */
  private clearPreviousSessionState(): void {
    this.logger.debug('Clearing previous session state (user + org caches)');
    this.userService.clearCache();
    this.organizationService.clearCache();
    this.currentUser.set(null);
    this.currentOrganization.set(null);
  }

  constructor() {
    // Load permissions IMMEDIATELY from token (if present)
    const token = this.getToken();
    if (token) {
      this.updatePermissionsFromToken(token);
    }
  }

  /**
   * Initiate login by redirecting to backend OAuth flow
   * @param invitationToken - Optional invitation token to accept after OAuth
   */
  login(invitationToken?: string): void {
    let authUrl = `${environment.apiUrl.replace('/api', '')}/auth/google`;

    // Pass invitation token as query param if provided
    if (invitationToken) {
      authUrl += `?invitation_token=${encodeURIComponent(invitationToken)}`;
    }

    // Redirect to backend which will handle OAuth flow
    navigateTo(authUrl, 'OAuth redirect');
  }

  /**
   * Test login (integration environment only)
   * Creates a simple test token without OAuth flow
   * @param email - Email address (must end with @test.com)
   */
  async testLogin(email: string): Promise<void> {
    if (!email.endsWith('@test.com')) {
      this.logger.error('Test auth only accepts @test.com emails');
      this.toast.error('Test login requires @test.com email');
      return;
    }

    this.logger.info('🧪 Test login for:', email);

    // CRITICAL: Clear stale user/org state from any previous login session
    // Without this, resolvers may use cached data from a different user
    this.clearPreviousSessionState();

    // Check for pending invitation token
    const invitationToken = this.sessionStorage.getItem('invitation_token');
    if (invitationToken) {
      this.logger.info('📧 Found pending invitation token:', invitationToken);
    }

    // Create simple test token for initial backend call
    const tempTestToken = `test:${email}`;

    // Backend may require an additional QA backdoor token alongside the
    // test: bearer token (see server.ts) -- attach it if we have one saved.
    const qaBackdoorToken = this.qaBackdoor.getToken();

    // Call backend to trigger user creation and get real JWT
    try {
      const response = await fetch(`${environment.apiUrl}/users/me`, {
        headers: {
          Authorization: `Bearer ${tempTestToken}`,
          ...(qaBackdoorToken ? { 'x-qa-backdoor-token': qaBackdoorToken } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      const userData = await response.json();
      this.logger.info('✅ Test user created/verified on backend:', userData);

      // Backend returns a real JWT token for test users
      if (!userData.token) {
        throw new Error('No JWT token received from backend');
      }

      // Store the real JWT token (same as OAuth flow)
      this.storeToken(userData.token);
      this.updatePermissionsFromToken(userData.token);
      this.logger.debug('✅ JWT token stored in localStorage');

      // Set user state
      this.currentUser.set({
        id: email,
        email: email,
        full_name: email.split('@')[0],
      });
      this.logger.info('🔓 Setting isAuthenticated = true (test login)');
      this.isAuthenticated.set(true);

      // If there's a pending invitation, accept it
      if (invitationToken) {
        this.logger.info('🔗 Accepting pending invitation...');
        try {
          const acceptResponse = await fetch(
            `${environment.apiUrl}/invitations/${invitationToken}/accept`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${userData.token}`,
              },
            },
          );

          const acceptResult = await acceptResponse.json();

          if (acceptResult.success) {
            this.logger.info('✅ Invitation accepted successfully');

            // Store the new JWT token with updated customer_id
            if (acceptResult.token) {
              this.storeToken(acceptResult.token);
              this.updatePermissionsFromToken(acceptResult.token);
              this.logger.info('✅ Updated JWT token with new organization');
            }

            // Clear the pending invitation token
            this.sessionStorage.removeItem('invitation_token');

            // Force page reload to reinitialize with new organization
            this.logger.info('🔄 Reloading page to switch to new organization...');
            navigateTo('/', 'switch organization — reinitialize with new customer_id');
            return; // Stop execution since page will reload
          } else {
            // Handle structured error response
            const errorMessages: Record<string, string> = {
              not_found: this.translate.translate('auth.invitation.error.notFound'),
              already_used: this.translate.translate('auth.invitation.error.alreadyUsed'),
              expired: this.translate.translate('auth.invitation.error.expired'),
              email_mismatch: this.translate.translate('auth.invitation.error.emailMismatch'),
              already_member: this.translate.translate('auth.invitation.error.alreadyMember'),
            };

            const errorMessage =
              errorMessages[acceptResult.reason] ||
              this.translate.translate('auth.invitation.error.generic');
            this.logger.error('❌ Invitation acceptance failed:', acceptResult.reason);
            this.toast.error(errorMessage);

            // Clear the invalid invitation token
            this.sessionStorage.removeItem('invitation_token');
          }
        } catch (error) {
          this.logger.error('💥 Error accepting invitation:', error);
          this.toast.error(this.translate.translate('auth.invitation.error.generic'));
          this.sessionStorage.removeItem('invitation_token');
        }
      }

      // Don't navigate here - let the login component handle navigation via reactive effect
      // The component will detect isAuthenticated=true and navigate to returnUrl or '/'
      if (userData.last_active_org) {
        this.logger.info(
          '✅ Existing user with org - login complete (navigation handled by component)',
        );
      } else {
        this.logger.info(
          '⚠️ New user without org - should redirect to signup (navigation handled by component)',
        );
      }

      // Note: Sync initialization happens via syncInitGuard on protected routes
    } catch (error) {
      this.logger.error('Test login failed:', error);
      this.toast.error('Test login failed. Check window.console for details.');
      this.clearToken();
    }
  }

  /**
   * Check if user is an active member of any organization
   *
   * @throws DatabaseError if database cannot be accessed (NOT an auth issue)
   * @returns true if user is active member, false if not or no token
   */
  async isActiveMemberOfOrg(): Promise<boolean> {
    try {
      await this.db.init();
      const result = await this.db.exec('SELECT is_active FROM users LIMIT 1');

      // User exists and is active
      return result.length > 0 && result[0].is_active === 1;
    } catch (error) {
      this.logger.debug('Database initialization failed:', error);

      // Check if this is an authentication error (no token) vs database error
      if (
        error instanceof Error &&
        (error.message.includes('Authentication required') ||
          error.message.includes('auth_token') ||
          error.message.includes('customer_id missing'))
      ) {
        // No auth token or malformed token - this is an auth issue
        this.logger.debug('Authentication error - no valid token');
        return false;
      }

      // Database error (connection, not found, etc) - THROW to let caller decide
      // This is NOT an authentication failure - token is valid but database unavailable
      this.logger.warn('Database error checking membership - throwing for caller to handle');
      throw error;
    }
  }

  /**
   * Handle OAuth callback with JWT token
   */
  async handleCallback(token: string): Promise<void> {
    // Prevent duplicate calls (ngOnInit can be called twice in dev mode)
    if (this.handleCallbackInProgress) {
      this.logger.warn('handleCallback already in progress - ignoring duplicate call');
      return;
    }

    this.handleCallbackInProgress = true;
    try {
      await this._handleCallback(token);
    } finally {
      this.handleCallbackInProgress = false;
    }
  }

  private async _handleCallback(token: string): Promise<void> {
    this.logger.debug('handleCallback started');

    // CRITICAL: Clear stale user/org state from any previous login session
    // Without this, resolvers may use cached data from a different user
    this.clearPreviousSessionState();

    this.logger.debug('Storing token in localStorage...');
    this.storeToken(token);
    this.updatePermissionsFromToken(token);
    this.logger.debug('Token stored');

    // Check if this is a new signup
    const payload = this.decodeJwt(token);
    const isNewSignup = payload.is_new_signup === true;
    const hasNoOrganization = !payload.customer_id;
    this.logger.debug('JWT decoded:', {
      sub: payload.sub,
      email: payload.email,
      isNewSignup,
      customer_id: payload.customer_id,
    });

    // If user has no organization, send them to signup page to create one
    if (hasNoOrganization && !isNewSignup) {
      this.logger.debug('User has no organization - routing to signup');
      this.router.navigate(['/signup']);
      return;
    }

    // If this is a new signup (new org creation), force a full page reload
    // to ensure the database service reinitializes with the new customer_id
    if (isNewSignup) {
      this.logger.debug('New signup detected - forcing page reload to switch databases');
      navigateTo('/onboarding', 'new signup — switch database');
      return; // Stop execution since page will reload
    }

    try {
      // Fetch full user profile from backend (includes avatar_url)
      this.logger.debug('Fetching user profile from backend...');
      const userProfile = await this.fetchUserProfile();
      this.logger.debug('User profile fetched:', {
        id: userProfile.id,
        email: userProfile.email,
        full_name: userProfile.full_name,
      });

      // Store in local database
      this.logger.debug('Storing user in local database...');
      await this.storeUserLocally(userProfile);
      this.logger.debug('User stored locally');

      // Set basic user state from JWT (resolver will load full details)
      this.logger.debug('Setting user state from JWT...');
      this.currentUser.set({
        id: payload.sub,
        email: payload.email,
        full_name: payload.full_name,
        avatar_url: userProfile.avatar_url,
      });
      this.isAuthenticated.set(true);
      this.logger.debug('User state set - resolver will load organization');

      // Check if user is active in this organization
      try {
        const result = await this.db.exec('SELECT is_active FROM users WHERE id = ?', [
          payload.sub,
        ]);

        if (result && result.length > 0 && result[0].is_active === 0) {
          this.logger.warn('User has been deactivated from this organization');

          // Only show the toast message once (not on every refresh)
          const hasBeenNotified = this.sessionStorage.getItem(this.DEACTIVATION_NOTIFIED_KEY);
          if (!hasBeenNotified) {
            this.toast.info(this.translate.translate('auth.userDeactivated'), false);
            this.sessionStorage.setItem(this.DEACTIVATION_NOTIFIED_KEY, 'true');
          }

          // Check if user has any organizations at all
          const organizations = await this.fetchUserOrganizations();

          if (organizations.length === 0) {
            // User has no organizations - clear token and redirect to create one
            this.logger.debug(
              'User has no organizations - clearing token and redirecting to signup',
            );
            this.clearToken();
            this.router.navigate(['/signup']);
            return;
          } else {
            // User has organizations but was deactivated from current one
            // Redirect to org selection to choose a different org
            this.logger.debug(
              'User deactivated from current org but has other orgs - redirecting to org selection',
            );
            this.router.navigate(['/organizations']);
            return;
          }
        }
      } catch (dbError) {
        // Database not ready yet - resolvers will handle deactivation check
        this.logger.debug('Could not check user activation status - resolvers will handle it');
      }

      // User is authenticated to their current org - continue to home
      // (They can switch orgs via the UI if needed)

      // Clear deactivation notification flag on successful login
      this.sessionStorage.removeItem(this.DEACTIVATION_NOTIFIED_KEY);
    } catch (error) {
      this.logger.error('Error in handleCallback:', error);
      this.logger.error('Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });

      // Show error toast ONCE - don't retry db.init() as it will fail again
      this.toast.error(this.translate.translate('auth.profileLoadFailed'));

      // Fallback: Use JWT data only (don't call loadUserFromToken - it will try db.init() again)
      this.logger.debug('Falling back to JWT-only mode (database unavailable)');
      const payload = this.decodeJwt(token);
      this.currentUser.set({
        id: payload.sub,
        email: payload.email,
        full_name: payload.full_name,
      });
      this.isAuthenticated.set(true);
      this.currentOrganization.set(null); // Can't load from db

      // Clear deactivation notification flag
      this.sessionStorage.removeItem(this.DEACTIVATION_NOTIFIED_KEY);
    }

    // Sync will be initialized by APP_INITIALIZER on next page load
    // No need to start it here

    // Navigate to home page for regular logins
    this.logger.debug('Navigating to home page...');
    this.router.navigate(['/']);
    this.logger.debug('handleCallback completed');
  }

  /**
   * Fetch user's organizations from backend
   */
  private async fetchUserOrganizations(): Promise<{ id: string; name: string; role: string }[]> {
    this.logger.debug('fetchUserOrganizations started');
    const token = this.getToken();
    if (!token) {
      this.logger.error('No authentication token available for fetching organizations');
      return [];
    }

    try {
      const response = await fetch(`${environment.apiUrl}/customers/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        this.logger.error('Failed to fetch user organizations:', response.status);
        return [];
      }

      const data = await response.json();
      this.logger.debug('User organizations fetched:', data.customers?.length || 0);
      return data.customers || [];
    } catch (error) {
      this.logger.error('Error fetching user organizations:', error);
      return [];
    }
  }

  /**
   * Fetch full user profile from backend
   */
  private async fetchUserProfile(): Promise<User> {
    this.logger.debug('fetchUserProfile started');
    const token = this.getToken();
    if (!token) {
      this.logger.error('No authentication token available');
      throw new Error('No authentication token');
    }
    this.logger.debug('Token retrieved from storage');

    // Use base URL without /api for auth endpoints
    const baseUrl = environment.apiUrl.replace('/api', '');
    const url = `${baseUrl}/auth/me`;
    this.logger.debug('Fetching from:', url);

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    this.logger.debug('Response received:', {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
    });

    if (!response.ok) {
      const errorText = await response.text();
      this.logger.error('Failed to fetch user profile:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error('Failed to fetch user profile');
    }

    const data = await response.json();
    this.logger.debug('User profile data:', { user: data.user });
    return data.user;
  }

  /**
   * Store user profile in local database
   */
  private async storeUserLocally(user: User): Promise<void> {
    await this.db.init();

    // OAuth callback can run before sync has applied schema on a fresh/local-reset DB.
    // In that case, skip local cache write and let post-sync resolvers load from DB.
    const usersTable = await this.db.exec(
      "SELECT name FROM sqlite_master WHERE type = 'table' AND name = 'users' LIMIT 1",
    );

    if (!usersTable || usersTable.length === 0) {
      this.logger.warn(
        'Skipping local user profile cache write: users table not ready yet (schema pending sync)',
      );
      return;
    }

    // IMPORTANT: Check if user already exists by EMAIL (not id)
    // The global user ID may differ from the customer database user ID
    // User records are synced from server with their original IDs
    const existing = await this.db.exec(
      'SELECT id, role, created_at FROM users WHERE email = ? AND deleted_at IS NULL LIMIT 1',
      [user.email],
    );

    if (existing && existing.length > 0) {
      // User already exists - update their profile but preserve id and role
      const existingUser = existing[0] as any;
      const now = new Date(this.clockSync.now()).toISOString();

      // Use exec() not run() — this is a local cache of the OAuth profile, not a
      // user-originated change. run() tracks writes for sync upload, but the
      // server is the authoritative source for user records.
      await this.db.exec(
        `INSERT OR REPLACE INTO users (
          id, email, full_name, avatar_url,
          oauth_provider, oauth_id,
          role, is_active,
          created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          existingUser.id, // Use existing ID (from server sync)
          user.email,
          user.full_name,
          user.avatar_url || null,
          'google',
          '',
          existingUser.role, // Preserve existing role (from server sync)
          1, // Always active during OAuth callback
          existingUser.created_at, // Preserve creation timestamp
          now,
        ],
      );
      this.logger.debug('Updated existing user profile in local database (preserved id and role)');
    } else {
      // New user - create with global user ID
      const now = new Date(this.clockSync.now()).toISOString();
      await this.db.exec(
        `INSERT OR REPLACE INTO users (
          id, email, full_name, avatar_url,
          oauth_provider, oauth_id,
          role, is_active,
          created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          user.id,
          user.email,
          user.full_name,
          user.avatar_url || null,
          'google',
          '',
          'user', // Default role for new users
          1,
          now,
          now,
        ],
      );
      this.logger.debug('Created new user profile in local database');
    }
  }

  /**
   * Log out the current user
   */
  async logout(): Promise<void> {
    this.logger.info('🔒 User logout initiated');

    // ── Synchronous steps (must complete before any async work) ──────────────

    // Step 0: Clear permissions immediately so UI guards see clean state right away
    this.permissionService.clearPermissions();
    this.logger.debug('✅ Permissions cleared');

    // Step 1: Stop sync service first to prevent further message processing
    try {
      this.customSyncService.stop();
      this.logger.debug('✅ Sync service stopped');
    } catch (error) {
      this.logger.error('Error stopping sync service on logout:', error);
    }

    // Step 2: Clear auth token — removes auth context immediately
    this.clearToken();
    this.logger.debug('✅ Auth token cleared');

    // Step 3: Clear all cached user/org state
    this.clearPreviousSessionState();
    this.logger.debug('✅ User/org cache cleared');

    // Step 4: Navigate to login immediately (synchronous) so the user sees the
    // login page right away, before any slow async cleanup below.
    this.router.navigate(['/login']);
    this.logger.info('🔓 User navigated to login');

    // ── Async cleanup (runs after navigation, in the background) ─────────────

    // Step 5: Wait briefly to allow in-flight sync operations to finish
    // before closing the DB (prevents "database is locked" errors)
    this.logger.debug('Waiting for sync handlers to complete (150ms delay)...');
    await new Promise((resolve) => setTimeout(resolve, 150));

    // Step 6: Close database connection
    try {
      await this.db.closeDatabase();
      this.logger.debug('✅ Database connection closed');
    } catch (error) {
      this.logger.error('Error closing database on logout:', error);
    }

    // Step 7: Clear user-scoped localStorage data
    this.userStorage.clearUserData();
    this.logger.debug('✅ User-scoped data cleared');

    // Step 8: Clear sessionStorage
    this.sessionStorage.clearAll();
    this.logger.debug('✅ Session storage cleared');

    this.logger.info('🔓 User logged out successfully');
  }

  /**
   * Get the stored JWT token
   * Stored in localStorage to survive browser tab discarding (e.g., Brave)
   */
  getToken(): string | null {
    return this.localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Store the JWT token in localStorage
   * Using localStorage instead of sessionStorage because sessionStorage
   * is cleared when the browser discards/suspends tabs (common in Brave).
   * Token expiration (24h) provides the security boundary instead.
   */
  private storeToken(token: string): void {
    this.localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Refresh the current JWT token with the latest permissions from the server.
   * Called automatically when a 'token_refresh' WebSocket message is received
   * after a migration that modifies role_permissions has been applied.
   * Does NOT require the user to log out — the existing valid token is used
   * to authenticate the refresh request.
   */
  async refreshToken(): Promise<void> {
    const token = this.getToken();
    if (!token) {
      this.logger.warn('[refreshToken] No token to refresh');
      return;
    }

    try {
      this.logger.info('🔄 [refreshToken] Refreshing JWT permissions from server...');
      const response = await fetch(`${environment.apiUrl}/auth/token-refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        this.logger.error('[refreshToken] Server returned', response.status);
        return;
      }

      const data = await response.json();
      if (data.token) {
        this.storeToken(data.token);
        this.updatePermissionsFromToken(data.token);
        this.logger.info('✅ [refreshToken] JWT refreshed — permissions updated');
      }
    } catch (error) {
      this.logger.error('[refreshToken] Failed to refresh token:', error);
      // Non-fatal: user can still work; permissions will be stale until next login
    }
  }

  /**
   * Create a new organization for the current authenticated user
   * @param organizationName - Name of the organization to create
   * @returns true if successful, false otherwise
   */
  async createOrganization(organizationName: string): Promise<boolean> {
    const token = this.getToken();
    if (!token) {
      this.logger.error('No authentication token available');
      return false;
    }

    try {
      const response = await fetch(`${environment.apiUrl}/organizations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: organizationName }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.logger.error('Failed to create organization:', response.status, errorText);
        return false;
      }

      const data = await response.json();
      this.logger.info('✅ Organization created:', data);

      // Store the new JWT token (contains new customer_id and permissions)
      if (data.token) {
        this.storeToken(data.token);
        this.updatePermissionsFromToken(data.token);

        // Decode token to get user info
        const payload = this.decodeJwt(data.token);

        // Set user state (same as what initialize() would do)
        this.currentUser.set({
          id: payload.sub,
          email: payload.email,
          full_name: payload.full_name,
        });
        this.isAuthenticated.set(true);
      }

      // Set organization name immediately from response
      if (data.orgName) {
        this.currentOrganization.set(data.orgName);
        this.logger.info('✅ Organization name set:', data.orgName);
      }

      // Organization will be synced in background via custom sync
      // OrganizationResolver will load it when routing to protected pages

      return true;
    } catch (error) {
      this.logger.error('Error creating organization:', error);
      return false;
    }
  }

  /**
   * Get list of organizations the current user is a member of
   * @returns Array of organizations with customer_id, name, role, etc.
   */
  async getUserOrganizations(): Promise<
    { customer_id: string; name: string; role: string; database_name: string }[] | null
  > {
    const token = this.getToken();
    if (!token) {
      this.logger.error('No authentication token available');
      return null;
    }

    const payload = this.decodeJwt(token);
    this.logger.info('🔍 Fetching organizations for user:', payload.sub, 'email:', payload.email);

    try {
      const response = await fetch(`${environment.apiUrl}/customers/my-organizations`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.logger.error('Failed to fetch user organizations:', response.status, errorText);
        return null;
      }

      const data = await response.json();
      this.logger.info('📋 User organizations:', data.organizations);
      this.logger.debug('📦 Full backend response:', JSON.stringify(data, null, 2));

      return data.organizations || [];
    } catch (error) {
      this.logger.error('Error fetching user organizations:', error);
      return null;
    }
  }

  /**
   * Switch to a different organization and get new JWT with that customer_id
   * @param customerId - The customer_id to switch to
   * @returns true if successful, false otherwise
   */
  async switchOrganization(customerId: string): Promise<boolean> {
    const token = this.getToken();
    if (!token) {
      this.logger.error('No authentication token available');
      return false;
    }

    try {
      const response = await fetch(`${environment.apiUrl}/customers/switch`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customer_id: customerId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.logger.error('Failed to switch organization:', response.status, errorText);
        return false;
      }

      const data = await response.json();
      this.logger.info('🔄 Switched to organization:', customerId);

      // Store the new JWT token (contains new customer_id)
      if (data.token) {
        this.storeToken(data.token);
        this.updatePermissionsFromToken(data.token);
        return true;
      }

      this.logger.error('No token returned from switch organization endpoint');
      return false;
    } catch (error) {
      this.logger.error('Error switching organization:', error);
      return false;
    }
  }

  /**
   * Get the current user's ID
   */
  getCurrentUserId(): string | null {
    return this.currentUser()?.id || null;
  }

  /**
   * Get the current user
   */
  getCurrentUser(): User | null {
    return this.currentUser();
  }

  /**
   * Check if a JWT token is valid (not expired and well-formed)
   * @param token - JWT token to validate
   * @returns true if valid, false if expired or malformed
   */
  isTokenValid(token: string): boolean {
    try {
      const payload = this.decodeJwt(token);

      // Check if token has expiration claim
      if (!payload.exp) {
        this.logger.warn('Token missing expiration claim');
        return false;
      }

      // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
      const now = Math.floor(this.clockSync.now() / 1000);
      const isExpired = payload.exp < now;

      if (isExpired) {
        const expiredDate = new Date(payload.exp * 1000).toISOString();
        this.logger.info(`Token expired at ${expiredDate}`);
        return false;
      }

      return true;
    } catch (error) {
      this.logger.error('Token validation failed:', error);
      return false;
    }
  }

  /**
   * Clear the authentication token with logging
   * CRITICAL: Always log token removals for debugging
   */
  clearToken(): void {
    this.logger.warn('🚨 CLEARING AUTH TOKEN', new Error().stack);
    this.localStorage.removeItem(this.TOKEN_KEY);
    this.currentUser.set(null);
    this.isAuthenticated.set(false);
    this.permissionService.clearPermissions();
  }

  /**
   * Decode JWT token to extract payload
   * Public method to allow other services (like SyncService) to access JWT claims
   */
  decodeJwt(token: string): JwtPayload {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT token format');
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(jsonPayload);
  }

  /**
   * Extract and set permissions from JWT token
   * Called whenever a new token is received (login, org switch, etc.)
   */
  private updatePermissionsFromToken(token: string): void {
    try {
      const payload = this.decodeJwt(token);
      const permissions = payload.permissions || {};
      const isSuperAdmin = payload.isSuperAdmin || false;

      // Log permissions for debugging
      this.logger.info('🔐 Setting permissions from JWT:', {
        permissionCount: Object.keys(permissions).length,
        isSuperAdmin,
        permissions: permissions,
      });

      this.permissionService.setPermissions(permissions, isSuperAdmin);
    } catch (error) {
      this.logger.error('Failed to extract permissions from token:', error);
      // Clear permissions on error
      this.permissionService.clearPermissions();
    }
  }
}
